import { useState, useEffect } from 'react';
import { X, Trophy, Crown, Medal, Flame, Zap, ChevronUp, ChevronDown, RefreshCw } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface LeaderboardEntry {
  user_id: string;
  display_name: string;
  overall_streak: number;
  total_xp: number;
  league: string;
  rank: number;
  is_current_user?: boolean;
}

interface LeaderboardModalProps {
  onClose: () => void;
  currentUserId: string;
}

export default function LeaderboardModal({ onClose, currentUserId }: LeaderboardModalProps) {
  const [leaderboardData, setLeaderboardData] = useState<LeaderboardEntry[]>([]);
  const [userPosition, setUserPosition] = useState<LeaderboardEntry | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [filter, setFilter] = useState<'all' | 'bronze' | 'silver' | 'gold' | 'diamond'>('all');

  useEffect(() => {
    loadLeaderboard();
  }, [filter]);

  const loadLeaderboard = async () => {
    try {
      setIsLoading(true);

      const { data: leaderboard, error: leaderboardError } = await supabase.rpc('get_leaderboard_rankings', {
        p_limit: 50
      });

      if (leaderboardError) throw leaderboardError;

      let filteredData = leaderboard || [];
      if (filter !== 'all') {
        const filterLeague = filter.charAt(0).toUpperCase() + filter.slice(1);
        filteredData = filteredData.filter((entry: LeaderboardEntry) => entry.league === filterLeague);
      }

      setLeaderboardData(filteredData);

      const userEntry = filteredData.find((entry: LeaderboardEntry) => entry.user_id === currentUserId);
      if (userEntry) {
        setUserPosition(userEntry);
      }
    } catch (error) {
      console.error('Error loading leaderboard:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await supabase.rpc('update_user_stats', { p_user_id: currentUserId });
    await loadLeaderboard();
    setIsRefreshing(false);
  };

  const getLeagueBadgeColor = (league: string) => {
    switch (league) {
      case 'Diamond':
        return 'from-cyan-400 to-blue-500';
      case 'Gold':
        return 'from-yellow-400 to-orange-500';
      case 'Silver':
        return 'from-gray-300 to-gray-500';
      default:
        return 'from-amber-600 to-amber-800';
    }
  };

  const getRankIcon = (rank: number) => {
    if (rank === 1) return <Crown className="w-5 h-5 text-yellow-500" />;
    if (rank === 2) return <Medal className="w-5 h-5 text-gray-400" />;
    if (rank === 3) return <Medal className="w-5 h-5 text-amber-600" />;
    return null;
  };

  const getLeagueIcon = (league: string) => {
    switch (league) {
      case 'Diamond':
        return '💎';
      case 'Gold':
        return '🏆';
      case 'Silver':
        return '🥈';
      default:
        return '🥉';
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-2 sm:p-4 animate-fadeIn">
      <div className="bg-white rounded-2xl sm:rounded-3xl w-full max-w-2xl max-h-[95vh] sm:max-h-[90vh] overflow-hidden flex flex-col animate-scaleIn">
        {/* Header */}
        <div className="relative bg-gradient-to-br from-cyan-400 via-blue-400 to-purple-400 p-4 sm:p-6">
          <button
            onClick={onClose}
            className="absolute top-3 right-3 sm:top-4 sm:right-4 w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-white/20 backdrop-blur-sm hover:bg-white/30 flex items-center justify-center transition-colors"
          >
            <X size={18} className="text-white sm:w-5 sm:h-5" />
          </button>

          <div className="flex items-center gap-2 sm:gap-3 mb-2">
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
              <Trophy size={20} className="text-white sm:w-6 sm:h-6" />
            </div>
            <div>
              <h2 className="text-xl sm:text-2xl font-black text-white">Global Leaderboard</h2>
              <p className="text-xs sm:text-sm text-white/80">Top performers by streak</p>
            </div>
          </div>

          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="mt-3 sm:mt-4 flex items-center gap-2 px-3 sm:px-4 py-2 bg-white/20 backdrop-blur-sm rounded-xl text-white text-xs sm:text-sm font-bold hover:bg-white/30 transition-colors disabled:opacity-50"
          >
            <RefreshCw size={14} className={`sm:w-4 sm:h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            {isRefreshing ? 'Refreshing...' : 'Refresh Rankings'}
          </button>
        </div>

        {/* Filter Tabs */}
        <div className="flex gap-2 px-4 sm:px-6 pt-3 sm:pt-4 overflow-x-auto scrollbar-hide">
          {['all', 'diamond', 'gold', 'silver', 'bronze'].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f as any)}
              className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg sm:rounded-xl text-xs sm:text-sm font-bold whitespace-nowrap transition-colors ${
                filter === f
                  ? 'bg-cyan-500 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>

        {/* Leaderboard List */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          {isLoading ? (
            <div className="space-y-3">
              {[...Array(10)].map((_, i) => (
                <div key={i} className="bg-gray-100 rounded-xl h-20 animate-pulse" />
              ))}
            </div>
          ) : leaderboardData.length === 0 ? (
            <div className="text-center py-12">
              <Trophy className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 font-semibold">No leaderboard data yet</p>
              <p className="text-sm text-gray-400 mt-2">Start tracking to appear on the leaderboard!</p>
            </div>
          ) : (
            <div className="space-y-2 sm:space-y-3">
              {leaderboardData.map((entry) => {
                const isCurrentUser = entry.user_id === currentUserId;
                return (
                  <div
                    key={entry.user_id}
                    className={`relative rounded-lg sm:rounded-xl p-3 sm:p-4 border-2 transition-all ${
                      isCurrentUser
                        ? 'bg-cyan-50 border-cyan-400 shadow-lg'
                        : 'bg-white border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center gap-2 sm:gap-4">
                      {/* Rank */}
                      <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center flex-shrink-0">
                        {getRankIcon(entry.rank) || (
                          <span className="text-base sm:text-lg font-black text-gray-700">#{entry.rank}</span>
                        )}
                      </div>

                      {/* User Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1 sm:gap-2 mb-1">
                          <h3 className="font-bold text-sm sm:text-base text-gray-900 truncate">
                            {entry.display_name}
                          </h3>
                          {isCurrentUser && (
                            <span className="text-[10px] sm:text-xs bg-cyan-500 text-white px-1.5 sm:px-2 py-0.5 rounded-full flex-shrink-0">
                              You
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 sm:gap-3 text-xs sm:text-sm">
                          <div className="flex items-center gap-1">
                            <Flame className="w-3 h-3 sm:w-4 sm:h-4 text-orange-500 flex-shrink-0" />
                            <span className="font-bold text-gray-700">{entry.overall_streak}d</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Zap className="w-3 h-3 sm:w-4 sm:h-4 text-cyan-500 flex-shrink-0" />
                            <span className="font-bold text-gray-700">{entry.total_xp}</span>
                          </div>
                        </div>
                      </div>

                      {/* League Badge */}
                      <div className={`px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg bg-gradient-to-br ${getLeagueBadgeColor(entry.league)} flex items-center gap-1 sm:gap-1.5 flex-shrink-0`}>
                        <span className="text-sm sm:text-lg">{getLeagueIcon(entry.league)}</span>
                        <span className="text-[10px] sm:text-xs font-black text-white hidden sm:inline">{entry.league}</span>
                      </div>
                    </div>

                    {/* Rank change indicator for top 3 */}
                    {entry.rank <= 3 && (
                      <div className="absolute -top-1 -right-1 w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center border-2 border-white">
                        <Trophy className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-white" />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* User Position Footer */}
        {userPosition && !isLoading && (
          <div className="border-t border-gray-200 p-4 sm:p-6 bg-gray-50">
            <div className="text-center mb-2 sm:mb-3">
              <p className="text-xs sm:text-sm font-bold text-gray-600 uppercase tracking-wider mb-1">Your Rank</p>
              <div className="flex items-center justify-center gap-2 sm:gap-3">
                <span className="text-3xl sm:text-4xl font-black text-gray-900">#{userPosition.rank}</span>
                <div className="text-left">
                  <div className="flex items-center gap-1 sm:gap-1.5">
                    <Flame className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-orange-500" />
                    <span className="font-bold text-gray-700 text-sm sm:text-base">{userPosition.overall_streak} days</span>
                  </div>
                  <div className="flex items-center gap-1 sm:gap-1.5">
                    <Zap className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-cyan-500" />
                    <span className="font-bold text-gray-700 text-sm sm:text-base">{userPosition.total_xp} XP</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Progress to next rank */}
            {userPosition.rank > 1 && leaderboardData[0] && (
              <div className="text-center text-xs sm:text-sm text-gray-500">
                <p className="mb-1 font-semibold">
                  {leaderboardData[0].overall_streak - userPosition.overall_streak > 0 ? (
                    <>
                      <span className="text-orange-600 font-bold">
                        {leaderboardData[0].overall_streak - userPosition.overall_streak}
                      </span>
                      {' '}more days to reach #1
                    </>
                  ) : (
                    'Keep up your streak to maintain your rank!'
                  )}
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .animate-fadeIn {
          animation: fadeIn 0.2s ease-out;
        }

        @keyframes scaleIn {
          from {
            opacity: 0;
            transform: scale(0.9);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        .animate-scaleIn {
          animation: scaleIn 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
        }

        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </div>
  );
}
